import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ModalBusyContainer from 'components/ModalBusyContainer';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { actions as modalActions } from 'redux/modal';
import { getConfirmationModalConfig, resolveApiErrorMessage } from 'utils';
import { getTrainingSessionAssignableStudents, assignTrainingSessionParticipants } from 'http/trainingSessions';

const useStyles = makeStyles(theme => ({
  btnContainer: {
    marginTop: '24px',
  },
  btn: {
    minWidth: '140px',
  },
  tableWrap: {
    maxHeight: '380px',
    overflowY: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
  },
  filter: {
    marginBottom: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  errorMessage: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
}));

const SORTABLE_FIELDS = ['ids', 'fullName', 'email', 'station'];

const normalizeValue = value => (value || '').toString().trim().toLowerCase();

const AssignParticipantsModal = ({
  trainingSessionId,
  onAssigned,
  closeModal,
  initialSelectedStudentIds = [],
  initialError = null,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const [students, setStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState(initialSelectedStudentIds);
  const [submitError, setSubmitError] = useState(initialError);
  const [filterValue, setFilterValue] = useState('');
  const [sorting, setSorting] = useState({
    field: 'fullName',
    direction: 'asc',
  });

  const [fetchStudentsState, fetchStudents] = useAsyncFn(async () => {
    const response = await getTrainingSessionAssignableStudents(trainingSessionId);
    if (!response.error) {
      setStudents(response.items || []);
    }
    return response;
  }, [trainingSessionId]);

  useEffect(() => {
    fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleStudents = useMemo(() => {
    const normalizedFilter = normalizeValue(filterValue);
    const filteredStudents = normalizedFilter
      ? students.filter(student => {
          return [student.fullName, student.email].some(value => normalizeValue(value).includes(normalizedFilter));
        })
      : students;

    return [...filteredStudents].sort((left, right) => {
      const { field, direction } = sorting;
      const order = direction === 'asc' ? 1 : -1;
      return normalizeValue(left[field]).localeCompare(normalizeValue(right[field])) * order;
    });
  }, [filterValue, sorting, students]);

  const visibleStudentIds = useMemo(() => visibleStudents.map(student => student.id), [visibleStudents]);
  const selectedCount = selectedStudentIds.length;
  const isAllSelected = visibleStudentIds.length > 0 && visibleStudentIds.every(id => selectedStudentIds.includes(id));
  const isLoading = fetchStudentsState.loading;

  const toggleStudent = studentId => {
    setSubmitError(null);
    setSelectedStudentIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      }
      return [...prev, studentId];
    });
  };

  const toggleSelectAll = checked => {
    setSubmitError(null);
    if (checked) {
      setSelectedStudentIds(prev => Array.from(new Set([...prev, ...visibleStudentIds])));
      return;
    }
    setSelectedStudentIds(prev => prev.filter(id => !visibleStudentIds.includes(id)));
  };

  const selectedStudentsText = useMemo(() => {
    return t('courseDetails.assignStudentsCount', { count: selectedCount });
  }, [selectedCount, t]);

  const handleSorting = field => {
    if (!SORTABLE_FIELDS.includes(field)) {
      return;
    }

    setSorting(prev => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }

      return {
        field,
        direction: 'asc',
      };
    });
  };

  const renderHeader = (title, field) => {
    if (!SORTABLE_FIELDS.includes(field)) {
      return title;
    }

    return (
      <TableSortLabel
        active={sorting.field === field}
        direction={sorting.field === field ? sorting.direction : 'asc'}
        onClick={() => handleSorting(field)}
      >
        {title}
      </TableSortLabel>
    );
  };

  const reopenAssignModal = (errorMessage, selectedIds) => {
    dispatch(
      modalActions.openModal(
        { size: 'large', title: t('trainingSessions.assignParticipantsTitle') },
        <AssignParticipantsModal
          trainingSessionId={trainingSessionId}
          onAssigned={onAssigned}
          closeModal={closeModal}
          initialSelectedStudentIds={selectedIds}
          initialError={errorMessage}
        />,
      ),
    );
  };

  const showSummary = response => {
    dispatch(
      modalActions.openModal(
        { size: 'small', title: t('courseDetails.assignStudentsSummaryTitle') },
        <p>
          {t('courseDetails.assignStudentsSummaryMessage', {
            assigned: response.assigned || 0,
            failed: response.failed || 0,
          })}
        </p>,
      ),
    );
  };

  const handleSubmit = async () => {
    if (!selectedCount) {
      return;
    }
    setSubmitError(null);
    const selectedIds = [...selectedStudentIds];
    const [config, element] = getConfirmationModalConfig({
      title: t('courseDetails.assignStudentsConfirmTitle'),
      confirmAction: async () => {
        const response = await assignTrainingSessionParticipants(trainingSessionId, {
          studentIds: selectedIds,
        });
        if (!response.error && !(response.statusCode && response.statusCode >= 400)) {
          dispatch(modalActions.closeModal());
          await onAssigned();
          showSummary(response);
          return;
        }

        const message = resolveApiErrorMessage(response.message, {
          t,
          fallbackMessage: t('errors.serverError'),
        });
        toast.error(message);
        reopenAssignModal(message, selectedIds);
      },
      rejectAction: () => reopenAssignModal(null, selectedIds),
      message: selectedStudentsText,
      confirmTitle: t('buttons.save'),
      rejectTitle: t('buttons.cancel'),
    });
    dispatch(modalActions.openModal(config, element));
  };

  return (
    <ModalBusyContainer busy={isLoading}>
      <TextField
        className={classes.filter}
        fullWidth
        variant="outlined"
        size="small"
        label={t('courseDetails.studentFilterLabel')}
        placeholder={t('courseDetails.studentFilterPlaceholder')}
        value={filterValue}
        onChange={event => setFilterValue(event.target.value)}
      />
      {visibleStudents.length > 0 ? (
        <FormControlLabel
          control={
            <Checkbox
              checked={isAllSelected}
              onChange={event => toggleSelectAll(event.target.checked)}
              color="primary"
            />
          }
          label={t('courseDetails.selectAllStudents')}
        />
      ) : null}
      <div className={classes.tableWrap}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>{renderHeader(t('tablesLabels.studentId'), 'ids')}</TableCell>
              <TableCell>{renderHeader(t('tablesLabels.name'), 'fullName')}</TableCell>
              <TableCell>{renderHeader(t('tablesLabels.email'), 'email')}</TableCell>
              <TableCell>{renderHeader(t('tablesLabels.station'), 'station')}</TableCell>
              <TableCell>{t('formLabels.jobsRoles')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!visibleStudents.length ? (
              <TableRow>
                <TableCell colSpan={6} className={classes.empty}>
                  {t('courseDetails.noAssignableStudents')}
                </TableCell>
              </TableRow>
            ) : (
              visibleStudents.map(student => (
                <TableRow key={student.id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedStudentIds.includes(student.id)}
                      onChange={() => toggleStudent(student.id)}
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>{student.ids || t('courseDetails.notSpecified')}</TableCell>
                  <TableCell>{student.fullName}</TableCell>
                  <TableCell>{student.email || t('courseDetails.notSpecified')}</TableCell>
                  <TableCell>{student.station || t('courseDetails.notSpecified')}</TableCell>
                  <TableCell>{(student.roles || []).map(role => role.name).join(', ')}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {submitError ? (
        <Typography color="error" className={classes.errorMessage}>
          {submitError}
        </Typography>
      ) : null}

      <Grid container spacing={3} className={classes.btnContainer}>
        <Grid item xs={6}>
          <Box textAlign="right">
            <Button
              className={classes.btn}
              type="button"
              color="primary"
              variant="contained"
              disabled={isLoading || !selectedCount}
              size="large"
              onClick={handleSubmit}
            >
              {t('buttons.save')}
            </Button>
          </Box>
        </Grid>
        <Grid item xs={6}>
          <Box textAlign="left">
            <Button
              className={classes.btn}
              variant="outlined"
              size="large"
              type="button"
              disabled={isLoading}
              onClick={closeModal}
            >
              {t('buttons.cancel')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </ModalBusyContainer>
  );
};

AssignParticipantsModal.propTypes = {
  trainingSessionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onAssigned: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  initialSelectedStudentIds: PropTypes.arrayOf(PropTypes.number),
  initialError: PropTypes.string,
};

AssignParticipantsModal.defaultProps = {
  initialSelectedStudentIds: [],
  initialError: null,
};

export default AssignParticipantsModal;
